import { RadioCard } from '@insify/design-system/components';
import { textXs } from '@insify/design-system/typography';
import { RichContentIntro } from '@insify/funnel-components';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useContent } from '../../../../hooks/useContent';

import { CreditCard } from './CreditCard';
import styles from './Payment.module.css';
import { Sepa } from './Sepa';

import type { CheckoutFormType } from '../..';
import type { PaymentMethod } from '../../../../models/queries';

type Props = {
  paymentMethods?: PaymentMethod[];
};

const paymentMethodsOrder: PaymentMethod[] = ['creditCard', 'sepa'];

const Payments = ({ paymentMethods }: Props) => {
  const { t } = useTranslation(['funnel']);
  const { register, formState } = useFormContext<CheckoutFormType>();

  const paymentMethodToComponent = useMemo(
    () => ({
      creditCard: {
        id: 'creditCard',
        trackingId: 'creditCard',
        value: 'creditCard' as PaymentMethod,
        label: t('funnel:checkout_credit_card_payment_method'),
        Component: CreditCard,
        ['data-testid']: 'credit-card-payment-method',
      },
      sepa: {
        id: 'sepa',
        trackingId: 'sepa',
        value: 'sepa' as PaymentMethod,
        label: t('funnel:checkout_iban_payment_method'),
        Component: Sepa,
      },
    }),
    [],
  );

  const orderedPaymentMethods = paymentMethodsOrder.filter((pm) => paymentMethods?.includes(pm));
  const items = useMemo(() => {
    return orderedPaymentMethods.map((paymentMethod) => {
      const { Component, ...attr } = paymentMethodToComponent[paymentMethod];
      return { ...attr, element: <Component className={styles.optionContent} /> };
    });
  }, [orderedPaymentMethods]);

  if (paymentMethods === undefined) {
    return null;
  }

  if (paymentMethods.length === 1) {
    const Component = paymentMethodToComponent[paymentMethods[0]].Component;
    return <Component />;
  }

  return (
    <RadioCard
      {...register('paymentMethod')}
      id='paymentMethod'
      items={items}
      error={formState.errors.paymentMethod?.message}
    />
  );
};

export const Payment = ({ paymentMethods }: Props) => {
  const { checkoutPayment } = useContent() || {};
  const { t } = useTranslation(['funnel']);

  return (
    <>
      <RichContentIntro className={styles.intro} content={checkoutPayment} />
      <Payments paymentMethods={paymentMethods} />
      <p className={clsx(textXs, styles.below)}>{t('funnel:checkout_payment_below')}</p>
    </>
  );
};
